import { Box, Loader } from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import Button from "@/design-system/components/Button";
import { GateNoticeCard } from "../GateNoticeCard";
import classes from "./index.module.css";

type PermissionsErrorStateProps = {
  /** Busca as permissões do setor de novo. */
  onRetry: () => void;
  /** A nova busca está em voo: o botão fica em espera. */
  isRetrying?: boolean;
  /**
   * O que falhou: as permissões (`/user/me`, padrão) ou o plano da conta
   * (`/billing/access-state`, SA-1361 ruling F2-7). Plano desconhecido não é
   * plano sem o recurso: o card troca o bloqueio de plano pelo erro.
   */
  subject?: "permissions" | "plan";
};

const COPY_BY_SUBJECT = {
  permissions: {
    eyebrow: "Permissões",
    title: "Não foi possível carregar suas permissões",
    body: "Verifique sua conexão e tente novamente. Seu acesso não foi alterado.",
  },
  plan: {
    eyebrow: "Plano",
    title: "Não foi possível carregar o plano da conta",
    body: "Verifique sua conexão e tente novamente. O plano da conta não foi alterado.",
  },
} as const;

/**
 * Falha ao carregar as permissões do setor ativo (depois dos retries), sem
 * dado do setor. Não é falta de permissão: mostrar o bloqueio de recurso ou o
 * aviso de administrador aqui diria ao admin que ele não pode nada (SA-1360).
 *
 * `role="alert"`: o card entra no lugar de um carregamento, e o leitor de tela
 * precisa ouvir a falha. O card fica montado durante o retry, e o botão NÃO é
 * desabilitado (o `loading` do Mantine desabilita, e o foco do teclado cairia
 * no <body>): em espera, ele ganha `aria-busy`, um spinner e ignora o clique.
 */
export function PermissionsErrorState({
  onRetry,
  isRetrying = false,
  subject = "permissions",
}: PermissionsErrorStateProps) {
  const copy = COPY_BY_SUBJECT[subject];
  return (
    <Box className={classes.wrapper}>
      <GateNoticeCard
        role="alert"
        icon={
          <IconAlertTriangle
            size={18}
            stroke={2}
          />
        }
        eyebrow={copy.eyebrow}
        title={copy.title}
        body={copy.body}>
        <Button
          className={classes.action}
          aria-busy={isRetrying}
          leftSection={
            isRetrying ? (
              <Loader
                size={14}
                color="currentColor"
              />
            ) : undefined
          }
          onClick={() => {
            if (!isRetrying) onRetry();
          }}>
          Tentar novamente
        </Button>
      </GateNoticeCard>
    </Box>
  );
}

export default PermissionsErrorState;
